import { useToast } from '@app/util/hooks/useToast'
import { useCallback, useEffect } from 'react'

/**
 * Debounced autosave primitive. Calls `save(value)` after `debounceDelayInSeconds`
 * of no changes; shows a fail toast if the save rejects or resolves falsy. Skips
 * saving while every value is empty (e.g. before a draft has any data). Shared by
 * the initiative and event creation wizards via thin per-entity wrappers.
 */
export const useAutoSave = <T extends object>(
  value: T,
  save: (value: T) => Promise<unknown>,
  debounceDelayInSeconds?: number,
) => {
  const debounceDelay = (debounceDelayInSeconds ?? 0.3) * 1000
  const { showFailToast } = useToast()

  const saveCallback = useCallback(() => {
    Promise.resolve(save(value))
      .then((success) => {
        if (!success) {
          showFailToast()
        }
      })
      .catch(() => {
        showFailToast()
      })
  }, [save, value, showFailToast])

  useEffect(() => {
    if (Object.values(value).every((v) => !v)) {
      return
    }

    const delayTimer = setTimeout(() => {
      saveCallback()
    }, debounceDelay)

    return () => {
      clearTimeout(delayTimer)
    }
  }, [value, debounceDelay, saveCallback])
}
